import { isValidElement, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useHover, useMergedRef } from "@mantine/hooks";
import { Accordion as MantineAccordion } from "@mantine/core";
import { componentWrapper } from "../../utilities";
import styles from "./accordion.module.scss";
import type {
    CustomAccordionControlFactory,
    CustomAccordionFactory,
    CustomAccordionItemFactory,
    CustomAccordionPanelFactory,
    IAccordionControlProps,
    IAccordionItemProps,
    IAccordionPanelProps,
    IProps,
    ISkeletonProps,
} from "./accordion.types";
import { Icon } from "../icon";
import { faChevronDown, faEllipsis, IconDefinition } from "@fortawesome/pro-solid-svg-icons";
import { AccordionContext, AccordionItemContext } from "./accordion.context";
import { Center } from "../center";
import { Text } from "../text";
import { Stack } from "../stack";
import { ActionIcon } from "../action-icon";
import { Group } from "../group";
import { AutoMenu } from "../auto-menu";
import { Skeleton } from "../skeleton";

/**
 * Accordion component for displaying collapsible content sections.
 * @param {boolean} props.hideChevron - Whether to hide the chevron icon on accordion items.
 * @returns {JSX.Element} The Accordion component.
 */
const Accordion = componentWrapper<CustomAccordionFactory, IProps>(
    "Accordion",
    ({ hideChevron = false, ...props }, ref) => {
        const [value, setValue] = useState<string[] | string | undefined>(props.multiple ? [] : undefined);
        const [openedMenu, setOpenedMenu] = useState<string | undefined>(undefined);
        const [hasBeenOpened, setHasBeenOpened] = useState<Set<string>>(new Set());

        const isItemActive = useCallback(
            (itemValue: string) => {
                if (!value) return false;
                return Array.isArray(value) ? value.includes(itemValue) : value === itemValue;
            },
            [value]
        );

        const hasItemBeenOpened = useCallback(
            (itemValue: string) => {
                return hasBeenOpened.has(itemValue);
            },
            [hasBeenOpened]
        );

        const isMenuOpened = useCallback(
            (itemValue: string) => {
                return openedMenu === itemValue;
            },
            [openedMenu]
        );

        const openMenu = useCallback((itemValue: string) => {
            setOpenedMenu(itemValue);
        }, []);

        const closeMenu = useCallback((itemValue: string) => {
            setOpenedMenu((prev) => (prev === itemValue ? undefined : prev));
        }, []);

        const toggleMenu = useCallback((itemValue: string) => {
            setOpenedMenu((prev) => (prev === itemValue ? undefined : itemValue));
        }, []);

        const onChange = useCallback(
            (newValue: string | string[] | null) => {
                setValue(newValue as any);

                if (newValue) {
                    const newSet = new Set(hasBeenOpened);
                    const values = Array.isArray(newValue) ? newValue : [newValue];
                    values.forEach((val) => newSet.add(val));
                    setHasBeenOpened(newSet);
                }
            },
            [hasBeenOpened]
        );

        return (
            <AccordionContext.Provider
                value={{
                    isItemActive,
                    hasItemBeenOpened,
                    isMenuOpened,
                    openMenu,
                    closeMenu,
                    toggleMenu,
                    variant: props.variant ?? "card",
                }}
            >
                <MantineAccordion
                    onChange={onChange as any}
                    value={value as any}
                    classNames={{
                        root: styles["root"],
                        item: styles["item"],
                        control: styles["control"],
                        chevron: hideChevron ? styles["chevron-hidden"] : styles["chevron"],
                        label: styles["label"],
                        icon: styles["icon"],
                        itemTitle: styles["itemTitle"],
                        panel: styles["panel"],
                        content: styles["content"],
                    }}
                    chevron={hideChevron ? null : <Icon size="sm" icon={faChevronDown} />}
                    {...props}
                />
            </AccordionContext.Provider>
        );
    }
);

/**
 * Individual accordion item component.
 * @returns {JSX.Element} The AccordionItem component.
 */
const AccordionItem = componentWrapper<CustomAccordionItemFactory, IAccordionItemProps>(
    "AccordionItem",
    (props, ref) => {
        return (
            <AccordionItemContext.Provider value={{ value: props.value }}>
                <MantineAccordion.Item {...props} ref={ref} />
            </AccordionItemContext.Provider>
        );
    }
);

/**
 * Icon component for accordion items.
 * @param {IconDefinition} props.icon - The icon to display.
 * @returns {JSX.Element} The AccordionIcon component.
 */
const AccordionIcon = ({ icon }: { icon: IconDefinition }) => {
    return <Icon size="xs" icon={icon} />;
};

/**
 * Accordion control component that displays the header content with optional icon, subtitle, and menu.
 * @param {IconDefinition} props.icon - Optional icon to display in the control.
 * @param {React.ReactNode} props.children - The main label content.
 * @param {string | string[] | ReactElement} props.subtitle - Optional subtitle displayed below the label. Text is cut
 *                                                          to one line and an array is joined with " · ". An element
 *                                                          is rendered as it is, across the control's full width
 *                                                          (except in the flat-card variant), so it can lay out its
 *                                                          own line.
 * @param {boolean} props.enabled - Whether the accordion item is enabled.
 * @param {Array} props.menu - Optional menu items to display on hover.
 * @param {React.ReactNode} props.rightSection - Optional content to display on the right side.
 * @param {boolean} props.active - Whether the accordion item is currently active.
 * @returns {JSX.Element} The AccordionControl component.
 */
const AccordionControl = componentWrapper<CustomAccordionControlFactory, IAccordionControlProps>(
    "AccordionControl",
    ({ icon, children, subtitle, enabled, menu, rightSection, active, ...props }, ref) => {
        const { hovered, ref: hoverRef } = useHover();
        const mergedRefs = useMergedRef(ref, hoverRef);
        const { variant, isMenuOpened, closeMenu, toggleMenu } = useContext(AccordionContext);
        const { value: itemValue } = useContext(AccordionItemContext);
        const opened = useMemo(() => isMenuOpened(itemValue), [isMenuOpened, itemValue]);
        const isElementSubtitle = isValidElement(subtitle);

        useEffect(() => {
            if (!hovered) {
                closeMenu(itemValue);
            }
        }, [hovered, closeMenu, itemValue]);

        const controlContent = useMemo(() => {
            /**
             * If you change this size, make sure to update the following vars in the styles module:
             * --accordion-header-font-size: var(--mantine-font-size-md);
             * --accordion-header-line-height: var(--mantine-line-height-md);
             */
            const controlContentTitleSize = "md";

            return (
                <>
                    <Stack className={styles["control-content"]} justify="center" miw={0}>
                        <Text
                            variant="semibold"
                            size={controlContentTitleSize}
                            lineClamp={2}
                            className={styles["control-title"]}
                            data-testid="accordion-control-label"
                        >
                            {children}
                        </Text>
                    </Stack>
                    {isElementSubtitle ? (
                        <Text
                            component="div"
                            variant="medium"
                            size="xs"
                            dimmed
                            className={styles["subtitle"]}
                            data-testid="accordion-control-subtitle"
                        >
                            {subtitle}
                        </Text>
                    ) : (
                        subtitle && (
                            <Text
                                variant="medium"
                                size="xs"
                                dimmed
                                className={styles["subtitle"]}
                                lineClamp={1}
                                data-testid="accordion-control-subtitle"
                            >
                                {Array.isArray(subtitle) ? subtitle.join(" · ") : subtitle}
                            </Text>
                        )
                    )}
                </>
            );
        }, [children, isElementSubtitle, subtitle]);
        return (
            <MantineAccordion.Control
                data-enabled={enabled || undefined}
                data-active={active}
                icon={icon && <AccordionIcon icon={icon} data-testid={`accordion-control-icon`} />}
                {...props}
                ref={mergedRefs}
            >
                <Group className={styles["control-root"]} justify="space-between" wrap="nowrap">
                    {variant === "flat-card" ? (
                        <Group flex={1} justify="space-between" gap="xs" miw={0}>
                            {controlContent}
                        </Group>
                    ) : (
                        // An element subtitle gets the control's full width, so it can measure what fits on its line.
                        // The flat-card variant above keeps the title and subtitle on one row instead.
                        <Stack
                            gap="xs"
                            miw={0}
                            flex={isElementSubtitle ? 1 : undefined}
                            data-testid="accordion-control-title-stack"
                        >
                            {controlContent}
                        </Stack>
                    )}
                    {rightSection}

                    {menu && (
                        <Center
                            onClick={(e) => e.stopPropagation()}
                            style={{ opacity: hovered ? 1 : 0 }}
                            className={styles["control-menu"]}
                        >
                            <AutoMenu
                                opened={opened}
                                onClose={() => closeMenu(itemValue)}
                                onDismiss={() => closeMenu(itemValue)}
                                items={menu}
                                keepMounted
                                withinPortal={false}
                                data-testid="accordion-actions-menu-dropdown"
                            >
                                <ActionIcon
                                    onClick={() => toggleMenu(itemValue)}
                                    size="md"
                                    variant="subtle"
                                    color="gray"
                                    icon={faEllipsis}
                                    data-testid="accordion-actions-menu"
                                />
                            </AutoMenu>
                        </Center>
                    )}
                </Group>
            </MantineAccordion.Control>
        );
    }
);

/**
 * Accordion panel component that renders the children when the accordion item is opened or when the renderChildren prop is true.
 * @param {boolean} props.mountWhenOpened - Whether to mount the children when the accordion item is opened. If false, children will be null.
 * @param {React.ReactNode} props.children - The children to render.
 * @returns {JSX.Element} The AccordionPanel component.
 */
const AccordionPanel = componentWrapper<CustomAccordionPanelFactory, IAccordionPanelProps>(
    "AccordionPanel",
    ({ mountWhenOpened = true, children, ...props }, ref) => {
        const { value: itemValue } = useContext(AccordionItemContext);
        const { hasItemBeenOpened } = useContext(AccordionContext);

        const shouldMount = useMemo(
            () => hasItemBeenOpened(itemValue) || mountWhenOpened,
            [hasItemBeenOpened, itemValue, mountWhenOpened]
        );

        return (
            <MantineAccordion.Panel {...props} ref={ref}>
                {shouldMount ? children : null}
            </MantineAccordion.Panel>
        );
    }
);

/**
 * Skeleton loader for a card accordion: one placeholder per item, spaced, rounded and sized like a collapsed card
 * item, so the list keeps its shape while it loads.
 * @param {number} props.count - How many item placeholders to show.
 * @returns {JSX.Element} The AccordionSkeleton component.
 */
const AccordionSkeleton = ({ count = 3, ...props }: ISkeletonProps) => {
    return (
        <Stack className={styles["skeleton"]} data-testid="accordion-skeleton">
            {Array.from({ length: count }, (_, index) => (
                <Skeleton key={index} className={styles["skeleton-item"]} {...props} />
            ))}
        </Stack>
    );
};

Accordion.Item = AccordionItem;
Accordion.Control = AccordionControl;
Accordion.Panel = AccordionPanel;
Accordion.Chevron = MantineAccordion.Chevron;
Accordion.Icon = AccordionIcon;
Accordion.Skeleton = AccordionSkeleton;

export { Accordion, AccordionItem, AccordionControl, AccordionIcon, AccordionPanel, AccordionSkeleton };
